import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import Container from '../container';
import richTextParser from '../../utils/richtext-parser';
import Chevon from '../../images/svg/chevron-icon.svg';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import { format } from 'date-fns';
import { Link } from 'gatsby';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import mq from '../../utils/mq';
import { motion } from 'framer-motion';
import CtaWithArrow from '../cta-with-arrow';

const ResourcesAndFeaturedRegUpdateOuterContainer = styled.div`
  padding: 104px 0;
  background-color: #f8f8f8;
  ${mq.mobile(css`
    padding: 32px 0;
  `)};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  ${mq.mobile(css`
    display: flex;
    flex-direction: column;
    gap: 20px;
  `)};
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 6;
  padding: 40px 60px;
  background-color: #fff;
  border-radius: var(--Radius-Radius-3, 16px);
  ${mq.mobile(css`
    padding: 32px;
  `)};
`;

const RightColumn = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  grid-column: span 6;
  padding: 40px 60px;
  background-color: #fff;
  border-radius: var(--Radius-Radius-3, 16px);
  ${mq.mobile(css`
    padding: 32px;
  `)};
`;

const SectionTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.h2`
  color: #161616;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  max-width: 360px;
  text-wrap: pretty;
  margin-bottom: 18px;
`;

const SectionDescription = styled.div`
  color: var(--Foreground-fg-secondary, #565656);
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  text-wrap: pretty;
  margin-bottom: 40px;
`;

const ResourceCategoryLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 32px;
`;

const ResourceCategoryLinkChevron = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #c4c4c4;
  transition: all 0.3s ease;
  transition: transform 0.15s ease;
  svg {
    width: 14px;
    height: 14px;
    transform: rotate(180deg);
  }
`;

const ResourceCategoryLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: var(--Foreground-fg-primary, #111);
  width: 100%;
  justify-content: space-between;
  border-bottom: 1px solid #ebebeb;
  padding-bottom: 8px;
  padding-top: 12px;
  cursor: pointer;

  &:hover {
    ${ResourceCategoryLinkChevron} {
      color: #000;
      transform: translateX(2px);
    }
  }
`;

const ResourceCategoryLinkLabel = styled.span`
  color: var(--Foreground-fg-primary, #111);
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%;
`;

const ArticleCard = styled(motion.div)``;

const ArticleCardImage = styled(Link)`
  width: 100%;
  aspect-ratio: 16/9;
  position: relative;
  display: block;
  overflow: hidden;
  margin: 0;
  padding: 0;
  text-decoration: none;
  border-radius: var(--Radius-Radius-3, 16px);
`;

const ArticleCardImageWrapper = styled(motion.div)`
  will-change: transform;
  border-radius: var(--Radius-Radius-3, 16px);
  height: 100%;
`;

const ArticleCardDate = styled.div`
  color: #666;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 160%;
  padding-top: 28px;
  padding-bottom: 12px;
`;

const ArticleCardTitle = styled.h3`
  color: var(--Foreground-fg-primary, #111);
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  overflow: hidden;
  text-overflow: ellipsis;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
  padding-bottom: 28px;
  text-wrap: balance;
`;

export default function ResourcesAndFeaturedRegUpdate({
  resourceSectionTitle,
  resourceSectionDescription,
  resourceCategoryLinks,
  resourceCtaLabel,
  resourceCtaLink,
  regulatoryUpdateSectionTitle,
  regulatoryUpdateSectionDescription,
  regulatoryUpdateSectionArticle
}: {
  resourceSectionTitle: string;
  resourceSectionDescription: string;
  resourceCategoryLinks: any[];
  resourceCtaLabel: string;
  resourceCtaLink: string;
  regulatoryUpdateSectionTitle: string;
  regulatoryUpdateSectionDescription: string;
  regulatoryUpdateSectionArticle: any;
}) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <ResourcesAndFeaturedRegUpdateOuterContainer>
      <Container>
        <ContentGrid>
          <LeftColumn>
            <SectionTitle>{resourceSectionTitle}</SectionTitle>
            <SectionDescription
              dangerouslySetInnerHTML={{
                __html: richTextParser(resourceSectionDescription)
              }}
            />
            <ResourceCategoryLinksContainer>
              {resourceCategoryLinks.map((link) => (
                <ResourceCategoryLink
                  key={link.id}
                  to={parseStoryblokLink(link.link)}
                >
                  <ResourceCategoryLinkLabel>
                    {link.label}
                  </ResourceCategoryLinkLabel>
                  <ResourceCategoryLinkChevron>
                    <Chevon />
                  </ResourceCategoryLinkChevron>
                </ResourceCategoryLink>
              ))}
            </ResourceCategoryLinksContainer>
            <CtaWithArrow
              link={parseStoryblokLink(resourceCtaLink)}
              label={resourceCtaLabel}
            />
          </LeftColumn>
          <RightColumn>
            <SectionTitleContainer>
              <SectionTitle>{regulatoryUpdateSectionTitle}</SectionTitle>
              <SectionDescription
                dangerouslySetInnerHTML={{
                  __html: richTextParser(regulatoryUpdateSectionDescription)
                }}
              />
            </SectionTitleContainer>
            <ArticleCard
              onHoverStart={() => setIsHovered(true)}
              onHoverEnd={() => setIsHovered(false)}
            >
              <ArticleCardImage
                to={`/${regulatoryUpdateSectionArticle?.full_slug}`}
              >
                <ArticleCardImageWrapper
                  animate={
                    isHovered
                      ? {
                          scale: 1.03,
                          transition: { duration: 0.6, ease: [0.26, 1, 0.5, 1] }
                        }
                      : {
                          scale: 1,
                          transition: { duration: 0.3, ease: [0.26, 1, 0.5, 1] }
                        }
                  }
                >
                  <LazyImage
                    fluid={
                      regulatoryUpdateSectionArticle?.content?.gridImage &&
                      getFluidGatsbyImage(
                        regulatoryUpdateSectionArticle?.content?.gridImage
                      )
                    }
                    style={{
                      borderRadius: 'var(--Radius-Radius-3, 16px)',
                      height: '100%'
                    }}
                  />
                </ArticleCardImageWrapper>
              </ArticleCardImage>
              <ArticleCardDate>
                {regulatoryUpdateSectionArticle?.first_published_at &&
                  format(
                    new Date(
                      regulatoryUpdateSectionArticle?.first_published_at
                    ),
                    'dd MMM, yyyy'
                  )}
              </ArticleCardDate>
              <Link
                to={`/${regulatoryUpdateSectionArticle?.full_slug}`}
                style={{ textDecoration: 'none' }}
              >
                <ArticleCardTitle>
                  {regulatoryUpdateSectionArticle?.content?.customTitle}
                </ArticleCardTitle>
              </Link>
              <CtaWithArrow
                link={`/${regulatoryUpdateSectionArticle?.full_slug}`}
                label="Read now"
                isHovered={isHovered}
              />
            </ArticleCard>
          </RightColumn>
        </ContentGrid>
      </Container>
    </ResourcesAndFeaturedRegUpdateOuterContainer>
  );
}
